import { Link } from 'react-router-dom'
import { track } from '@/lib/analytics'
import { useLocale } from '@/i18n/locale'
import { NavLink } from '@/components/NavLink'
import './why-not-diy.css'

/**
 * Блок 7 «Почему не сами» — честное сравнение на главной (ТЗ 4.6a).
 *
 * Строки — переносом из сравнительной таблицы Positioning: те же пять
 * критериев, но словами собственника, а не инженера (аудит 2026-09-21:
 * «где живёт контекст», «галлюцинации», «KPI на P&L» и «внутренний
 * оператор» нетехнический читатель не расшифровывал). Смысл ячеек тот же,
 * новых доводов и цифр здесь нет. Левая колонка сводит две альтернативы
 * «своими силами» (ChatGPT по личным аккаунтам и самописное решение) в одну
 * формулировку. Полная таблица на четыре альтернативы живёт на
 * /vnedrenie-ii-agentov#sravnenie.
 */
const COPY = {
  ru: {
    eyebrow: 'Почему DNAI',
    heading: 'Собрать самим. Или с нами.',
    sub: 'Честное сравнение.',
    lede:
      'Внедрение ИИ в бизнес своими силами обычно начинается с личных аккаунтов ChatGPT или самописного решения. Знания остаются в личных чатах сотрудников и уходят вместе с человеком, ИИ каждый раз начинает с нуля, результат никто не принимает. Через три месяца — россыпь чатов, и ничего не накоплено.',
    cta: 'Записаться на диагностику',
    caption: 'Сравнение: внедрение ИИ своими силами и с DNAI',
    colMetric: 'Критерий',
    colDiy: 'Своими силами',
    colDnai: 'С DNAI',
    rows: [
      ['Где хранятся знания о компании', 'В личных аккаунтах сотрудников — уходят вместе с человеком', 'В общей системе компании — остаются у компании'],
      ['Можно ли верить цифрам', 'ИИ отвечает общими словами и может выдумать факт — никто не проверяет', 'Отвечает по вашим документам и показывает, откуда взял цифру'],
      ['Связь с деньгами', 'Нет — «впечатление от ИИ»', 'У каждой автоматизации — показатель в выручке или затратах'],
      ['Срок до результата', 'Сразу, но поверхностно — или месяцы непредсказуемо', '2–3 месяца по чек-листам с приёмкой'],
      ['Кто поддерживает', 'Сам сотрудник или только автор кода', 'Ваш сотрудник-Хранитель, обученный нами, плюс наш инженер'],
    ],
    full: 'Полное сравнение',
  },
  en: {
    eyebrow: 'Why DNAI',
    heading: 'Build in-house. Or with us.',
    sub: 'An honest comparison.',
    lede:
      'Adopting AI in-house usually starts with personal ChatGPT accounts or a homegrown build. The knowledge stays in employees’ private chats and leaves with the person, the AI starts from scratch every time, and nobody signs off on the result. Three months later — a scatter of chats, and nothing accumulated.',
    cta: 'Book AI diagnostics',
    caption: 'Comparison: adopting AI in-house versus with DNAI',
    colMetric: 'Criterion',
    colDiy: 'In-house',
    colDnai: 'With DNAI',
    rows: [
      ['Where company knowledge is kept', 'In employees’ personal accounts — leaves with the person', 'In the company’s own system — owned by the company'],
      ['Can you trust the numbers', 'The AI answers in generalities and may invent a fact — nobody checks', 'Answers from your documents and shows where each figure came from'],
      ['Link to money', 'None — “an impression of AI”', 'Every automation has a metric in revenue or costs'],
      ['Time to result', 'Instant, but shallow — or months, unpredictable', '2–3 months by checklist with sign-off'],
      ['Who maintains it', 'The employee themselves, or only the code’s author', 'Your in-house Keeper, trained by us, plus our engineer'],
    ],
    full: 'Full comparison',
  },
} as const

export function WhyNotDiy() {
  const locale = useLocale()
  const t = COPY[locale]
  const isEn = locale === 'en'

  return (
    <section id="why" className="section">
      <div className="container-x relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-10 xl:gap-x-16 gap-y-12 items-start">
          <div className="lg:col-span-5">
            <p className="font-mono text-small uppercase tracking-wider text-muted">{t.eyebrow}</p>
            <h2 className="why-heading mt-5 text-ink">{t.heading}</h2>
            <p className="mt-4 text-h3 font-normal text-muted">{t.sub}</p>
            <p className="mt-8 text-body text-muted leading-relaxed max-w-prose">{t.lede}</p>
            <NavLink
              href="#final-cta"
              className="btn-cta no-underline mt-9"
              onClick={() => track('architecture_cta_click')}
            >
              {t.cta} <span aria-hidden className="ml-2">→</span>
            </NavLink>
          </div>

          <div className="lg:col-span-7">
            {/* Таблица остаётся таблицей и на телефоне: display меняется на
                блочный, поэтому роли проставлены явно — иначе браузер снимает
                табличную семантику вместе с display: table. */}
            <table role="table" className="why-table">
              <caption className="sr-only">{t.caption}</caption>
              <thead role="rowgroup">
                <tr role="row">
                  <th role="columnheader" scope="col">{t.colMetric}</th>
                  <th role="columnheader" scope="col">{t.colDiy}</th>
                  <th role="columnheader" scope="col">{t.colDnai}</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                {t.rows.map(([metric, diy, dnai]) => (
                  <tr role="row" key={metric}>
                    <th role="rowheader" scope="row">{metric}</th>
                    <td role="cell" className="why-diy">
                      <span className="why-label" aria-hidden>{t.colDiy}</span>
                      {diy}
                    </td>
                    <td role="cell" className="why-dnai">
                      <span className="why-label" aria-hidden>{t.colDnai}</span>
                      <span className="why-value">
                        <span className="why-dot" aria-hidden />
                        {dnai}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Страница сравнения только русская — англоязычного посетителя
                на неё не уводим. */}
            {!isEn && (
              <p className="mt-5 text-small">
                <Link
                  to="/vnedrenie-ii-agentov#sravnenie"
                  className="text-accent no-underline hover:underline underline-offset-4"
                >
                  {t.full} <span aria-hidden>→</span>
                </Link>
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
